import { Menu, Portal, Transition } from "@headlessui/react";
import { EllipsisVerticalIcon } from "@heroicons/react/20/solid";
import React, { Fragment } from "react";
import { classNames } from "../utils/cn";
import { withMarginTw } from "../styles/withMargin";

const iconSizeMap = {
  s: "w-3 h-3",
  m: "w-4 h-4",
  l: "w-5 h-5",
};

export function TripleDotMenu(props: {
  children: React.ReactNode;
  width?: string;
  customButton?: React.ReactNode;
  mode?: "default" | "table";
  iconSize?: "s" | "m" | "l";
  m?: string;
}) {
  const margin = withMarginTw({ margin: props.m });
  const width = props.width ? props.width : "w-32";
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const [menuPosition, setMenuPosition] = React.useState<{
    top: number;
    left: number;
  } | null>(null);

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + window.scrollY,
      left:
        rect.right -
        (props.width ? parseInt(props.width.replace("w-", "")) * 4 : 128) +
        window.scrollX, // Converts e.g. w-32 -> 32 * 4 = 128px
    });
  };

  // Ensure the menu stays within the viewport after it renders
  React.useLayoutEffect(() => {
    if (!menuPosition || !menuRef.current) return;
    const el = menuRef.current;
    const rect = el.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const padding = 8; // small inset from edges

    let nextTop = menuPosition.top;
    let nextLeft = menuPosition.left;

    // Clamp vertically
    const minTop = scrollY + padding;
    const maxTop = scrollY + viewportHeight - rect.height - padding;
    if (nextTop > maxTop) nextTop = Math.max(minTop, maxTop);
    if (nextTop < minTop) nextTop = minTop;

    // Clamp horizontally
    const minLeft = scrollX + padding;
    const maxLeft = scrollX + viewportWidth - rect.width - padding;
    if (nextLeft > maxLeft) nextLeft = Math.max(minLeft, maxLeft);
    if (nextLeft < minLeft) nextLeft = minLeft;

    if (nextTop !== menuPosition.top || nextLeft !== menuPosition.left) {
      setMenuPosition({ top: nextTop, left: nextLeft });
    }
  }, [menuPosition]);

  if (props.mode === "table") {
    return (
      <Menu as="div" className="relative flex-none">
        <Menu.Button
          onClick={handleButtonClick}
          className="right-0 opacity-40 group-hover:opacity-100 -m-2.5 block p-2.5 text-gray-700 hover:text-gray-900"
        >
          <span className="sr-only">Open options</span>
          <EllipsisVerticalIcon
            className={props.iconSize ? iconSizeMap[props.iconSize] : "w-4 h-4"}
            aria-hidden="true"
          />
        </Menu.Button>
        {menuPosition && (
          <Portal>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
              afterLeave={() => setMenuPosition(null)}
            >
              <Menu.Items
                ref={menuRef}
                style={{
                  position: "absolute",
                  top: menuPosition.top,
                  left: menuPosition.left,
                }}
                className={`z-50 ${width} rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none`}
              >
                {props.children}
              </Menu.Items>
            </Transition>
          </Portal>
        )}
      </Menu>
    );
  }

  return (
    <Menu as="div" className={`relative flex-none ${margin}`}>
      {props.customButton ? (
        props.customButton
      ) : (
        <Menu.Button
          onClick={handleButtonClick}
          className="-m-2.5 block p-2.5 text-gray-400 hover:text-gray-900"
        >
          <span className="sr-only">Open options</span>
          <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
        </Menu.Button>
      )}
      {menuPosition && (
        <Portal>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
            afterLeave={() => setMenuPosition(null)}
          >
            <Menu.Items
              ref={menuRef}
              style={{
                position: "absolute",
                top: menuPosition.top,
                left: menuPosition.left,
              }}
              className={`z-50 ${width} rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none`}
            >
              {props.children}
            </Menu.Items>
          </Transition>
        </Portal>
      )}
    </Menu>
  );
}

// Add Button subcomponent
TripleDotMenu.Button = function TripleDotMenuButton(props: {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  text: string;
  variant?: "danger" | "normal" | "positive";
  icon?: React.ReactNode;
}) {
  const variantClasses = {
    danger: "text-red-500 hover:text-red-600",
    normal: "text-concrete-600 hover:text-concrete-700",
    positive: "text-blue-500 hover:text-blue-600",
  };

  return (
    <button
      type="button"
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        props.onClick(e);
      }}
      className={classNames(
        "px-2.5 py-2 hover:bg-gray-100 w-full text-left text-sm flex items-center gap-x-2",
        variantClasses[props.variant ?? "normal"],
      )}
    >
      {props.icon && <>{props.icon}</>}
      {props.text}
    </button>
  );
};
